import React, { useState, useMemo } from 'react';
import styles from './invoicesTab.module.css';
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import Select from "react-select";
import CalendarDropdown from '../../../dashboards/CalendarDropdown/CalendarDropdown';
import { useAppSelector } from "../../../../services/hooks";
import { adminSelector } from "../../../../services/selectors/user.selectors";
import { useGetPaymentsByCompanyIdQuery } from "../../../../services/api/utils.api";
import { IPayment } from "../../../../services/types/utils.types";
import Loader from "../../../loader/loader";

interface Invoice {
  id: string;
  date: string;
  amount: number;
  status: "paid" | "pending";
  planName: string | null;
  invoiceLink?: string;
}

interface InvoicesTabProps {}

export const InvoicesTab: React.FC<InvoicesTabProps> = () => {
  const admin = useAppSelector(adminSelector);
  const [statusFilter, setStatusFilter] = useState(-1);
  const [priceSort, setPriceSort] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  const { data: response, isLoading } = useGetPaymentsByCompanyIdQuery(
    { type: 1, companyId: admin.companyId },
    { skip: !admin.companyId }
  );

  const invoices: Invoice[] = useMemo(() => {
    if (!response?.data) return [];
    return response.data.map((payment: IPayment) => ({
      id: payment.invoiceNumber || payment.id.toString(),
      date: payment.timestamp,
      amount: payment.amount,
      status: payment.transactions && payment.transactions.length > 0 ? "paid" : "pending" as "paid" | "pending",
      planName: payment.planName,
      invoiceLink: payment.invoiceLink
    }));
  }, [response]);

  const statusOptions = [
    { value: -1, label: t("ALL_STATUSES") },
    { value: 0, label: t("INVOICE_PAID") },
    { value: 1, label: t("INVOICE_PENDING") }
  ];

  const sortOptions = [
    { value: 'asc', label: t("LOW_TO_HIGH") },
    { value: 'desc', label: t("HIGH_TO_LOW") }
  ];

  const filteredInvoices = useMemo(() => {
    let filtered = invoices.filter(invoice => {
      const matchesStatus = statusFilter === -1 ||
        (statusFilter === 0 && invoice.status === 'paid') ||
        (statusFilter === 1 && invoice.status === 'pending');
      const invoiceDate = new Date(invoice.date);
      const matchesDateRange = (!startDate || invoiceDate >= startDate) &&
                              (!endDate || invoiceDate <= endDate);
      return matchesStatus && matchesDateRange;
    });

    if (priceSort) {
      filtered = [...filtered].sort((a, b) => {
        return priceSort === 'asc' ? a.amount - b.amount : b.amount - a.amount;
      });
    }
    return filtered;
  }, [statusFilter, priceSort, startDate, endDate, invoices]);

  if (isLoading) {
    return (
      <div className={styles.invoicesTabContainer}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={styles.invoicesTabContainer}>
      <ul className={styles.invoiceFilters}>
        <li>
          <div className={styles.filterContainer}>
            <p className={styles.filterTitle}>{t("SORT_BY_PRICE")}</p>
            <div className={styles.selectContainer}>
              <Select
                name="priceSort"
                options={sortOptions}
                value={sortOptions.find(s => s.value === priceSort)}
                placeholder={t("SELECT_PLACEHOLDER")}
                className="customSelect"
                classNamePrefix="customSelect"
                isClearable={true}
                onChange={(value) => setPriceSort(value?.value ?? null)}
              />
            </div>
          </div>
        </li>
        <li>
          <div className={styles.filterContainer}>
            <CalendarDropdown
              title={t("DATE_RANGE")}
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              className={styles.filterTitle}
            />
          </div>
        </li>
        <li>
          <div className={styles.filterContainer}>
            <p className={styles.filterTitle}>{t("INVOICE_STATUS")}</p>
            <div className={styles.selectContainer}>
              <Select
                name="status"
                options={statusOptions}
                value={statusOptions.find(s => s.value === statusFilter)}
                placeholder={t("SELECT_PLACEHOLDER")}
                className="customSelect"
                classNamePrefix="customSelect"
                onChange={(value) => setStatusFilter(value?.value ?? -1)}
              />
            </div>
          </div>
        </li>
      </ul>
      <ul className="list">
        <li className={`listRow listRow_header ${styles.invoice}`}>
          <p className="listCell">{t("INVOICE_NUMBER")}</p>
          <p className="listCell">{t("INVOICE_DATE")}</p>
          <p className="listCell">{t("INVOICE_AMOUNT")}</p>
          <p className="listCell">{t("INVOICE_STATUS")}</p>
          <p className="listCell">{t("PLAN_NAME")}</p>
          <p className="listCell">{t("DOWNLOAD_INVOICE")}</p>
        </li>
        {filteredInvoices.map((invoice, index) => (
          <li
            key={invoice.id}
            className={`listRow ${styles.invoice} ${index % 2 === 0 ? "colored" : ""}`}
          >
            <p className="listCell">{invoice.id}</p>
            <p className="listCell">
              {new Date(invoice.date).toLocaleDateString("he-IL")}
            </p>
            <p className="listCell">₪{invoice.amount}</p>
            <p className="listCell">
              <span className={styles[invoice.status]}>
                {invoice.status === "paid"
                  ? t("INVOICE_PAID")
                  : t("INVOICE_PENDING")}
              </span>
            </p>
            <p className="listCell">{invoice.planName || "-"}</p>
            <p className="listCell">
              {invoice.invoiceLink ? (
                <a
                  href={invoice.invoiceLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${styles.invoiceLink} ${styles.downloadButton}`}
                  aria-label={t("DOWNLOAD_INVOICE")}
                />
              ) : (
                <button className={styles.downloadButton} aria-label={t("DOWNLOAD_INVOICE")} />
              )}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
};